import React, {
  useEffect,
  useContext,
  createContext,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

export const communityContext = createContext();

// Wrap the hook with a provider
export const ProvideCommunity = ({ children }) => {
  const inc = useProvideCommunity();
  // We are providing the object returned by useProvideCommunity as the value of the communityContext
  return <communityContext.Provider value={inc}>{children}</communityContext.Provider>;
};

ProvideCommunity.propTypes = {
  children: PropTypes.element,
};

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useCommunity = () => {
  // The context object will contain anything returned by useProvideCommunity
  const context = useContext(communityContext);
  if (context === undefined) {
    throw new Error('useCommunity must be used within a ProvideCommunity');
  }
  return context;
};

const initialState = {
  community: null,
  id: null,
  version: null,
  stale: true,
  loading: false,
  modifying: false,
  error: null,
  plans: [],
};

export const ACTIONS = Object.freeze({
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  SET_MODIFYING: 'SET_MODIFYING',
  SET_STALE: 'SET_STALE',
  SET_VERSION: 'SET_VERSION',
  SET_PLANS: 'SET_PLANS',
  UPDATE_PLAN: 'UPDATE_PLAN',
  DELETE_PLAN: 'DELETE_PLAN',
  SET_ID: 'SET_ID',
  DELETE: 'DELETE',
  RESET: 'RESET',
});


const communityReducer = (nextState, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return initialState;
    case ACTIONS.FETCH_INIT:
      return {
        ...nextState,
        loading: true,
        error: null,
      };
    case ACTIONS.FETCH_SUCCESS:
      if (action.payload.deleted) {
        return {
          ...nextState,
          loading: false,
          error: 'Community has been deleted',
          stale: false,
        };
      }

      return {
        ...nextState,
        loading: false,
        community: action.payload,
        plans: action.payload?.plans || [],
        stale: false,
      };
    case ACTIONS.FETCH_ERROR:
      return {
        ...nextState,
        loading: false,
        community: null,
        stale: false,
        error: 'Community not found',
      };
    case ACTIONS.UPDATE_PLAN:
    case ACTIONS.DELETE_PLAN:
      return {
        ...nextState,
        // community: action.payload,
        version: action.payload.version,
        stale: true,
      };
    case ACTIONS.DELETED:
      return {
        ...nextState,
        community: null,
        stale: true,
      };
    case ACTIONS.FETCH_FAILURE:
      return {
        ...nextState,
        loading: false,
        error: action.payload,
      };
    case ACTIONS.SET_ID: {
      let stale = nextState.id === action.payload ? false : true;
      return {
        ...nextState,
        stale,
        id: action.payload,
      };
    }
    case ACTIONS.SET_VERSION:
      return {
        ...nextState,
        version: action.payload,
      };
    case ACTIONS.SET_MODIFYING:
      return {
        ...nextState,
        modifying: action.payload,
      };
    case ACTIONS.SET_PLANS:
      return {
        ...nextState,
        plans: action.payload,
      };
    case ACTIONS.SET_STALE:
      return {
        ...nextState,
        stale: action.payload,
      };
    default:
      throw new Error(`communityReducer this action is not supported: ${action.type}`);
  }
};

// These are the properties and methods that the "useAuth" hook provides to its consumers
// We are providing the auth state, login, and logout methods
const useProvideCommunity = () => {
  const controller = useRef(null);
  //community state as a reducer
  const [communityState, dispatch] = useReducer(communityReducer, initialState);
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const deleteCommunity = async () => {
    if (!communityState.id) {
      enqueueSnackbar('Could not delete community', { variant: 'error'});
      return;
    }

    dispatch({ type: ACTIONS.SET_MODIFYING, payload: true });
    let variant = 'success';
    let message = 'Community successfully deleted';
    try {
      const response = await axios.post(`/api/community/delete`, {streamID: communityState.id});
      nav('/communities');
      return response;
    } catch (error) {
      console.log(error);
    } finally {
      enqueueSnackbar(message, { variant});
      dispatch({ type: ACTIONS.SET_MODIFYING, payload: false });
    }
  };

  const deletePlan = async (payload, name) => {
    dispatch({ type: ACTIONS.SET_MODIFYING, payload: true });
    // Assume success, handle failure in catch
    let variant = 'success';
    let message = `Plan "${name}" successfully removed`;

    try {
      const result = await axios.post('/api/community/deletePlan', payload);
      dispatch({ type: ACTIONS.DELETE_PLAN, payload: result.data });
      /*
        See backend repo src\shared_event_source\Events\events.js
        and src\shared_event_source\Events\community.js DeleteCommunityPlanEvent method
        for event Model
      */
    } catch (error) {
      variant = 'error';
      message = `Error removing plan "${name}"`;
      // Show an additional error message if we have one
      if (error?.response?.data?.error) {
        enqueueSnackbar(`${error.response.data.error.toString()}`, { variant});
      }
      console.error(error);
    } finally {
      enqueueSnackbar(message, { variant});
      dispatch({ type: ACTIONS.SET_MODIFYING, payload: false });
    }
  };

  const addOrEditPlan = async (plan, edit, successUrl, cancelUrl) => {
    dispatch({ type: ACTIONS.SET_MODIFYING, payload: true });
    try {
      const result = await axios.post(`/api/community/${edit ? 'updatePlan' : 'addPlan'}`, plan);
      if (result?.data?.streamID) {
        enqueueSnackbar(`Plan "${plan.name}" successfully ${edit ? 'updated' : 'created'}`, { variant: 'success'});
        nav(successUrl || cancelUrl);
        dispatch({ type: ACTIONS.UPDATE_PLAN, payload: result.data });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar(`Error creating plan "${plan.name}"`, { variant: 'error'});
    } finally {
      dispatch({ type: ACTIONS.SET_MODIFYING, payload: false });
    }
  };

  useEffect(() => {
    const {id, version, stale} = communityState;
    if (stale && id) {
      dispatch({type: ACTIONS.FETCH_INIT})
      let mounted = true;
      controller.current = new AbortController();
      const getCommunity = () => {
        axios.get(`/api/community/get?streamID=${id}${version === null ? '' : `&version=${version}`}`, { signal: controller.current.signal }).then(res => {
          if (mounted) {
            dispatch({type: ACTIONS.FETCH_SUCCESS, payload: res.data});
          }
        }
        ).catch(error => {
          if (error.name !== 'CanceledError') {
            dispatch({type: ACTIONS.FETCH_ERROR, payload: error});
          }
        });
      }

      getCommunity();

      // Cleanup on unmount
      return () => {
        controller.current.abort();
        mounted = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityState.stale, communityState.id]);

  return {
    unmount: () => {
      dispatch({type: ACTIONS.RESET});
      if (communityState.loading) {
        controller.current.abort();
      }
    },
    dispatch,
    addOrEditPlan,
    deletePlan,
    deleteCommunity,
    communityState,
  }
};
